import { ChatSheet } from "@/components/assistant-ui/chat-sheet";
import { PageTitle } from "@/components/layout/page-title";
import { AppSidebar } from "@/components/layout/sidebars/app-sidebar";
import { RightSidebarSwitcher } from "@/components/layout/right-sidebar-switcher";
import { NavUser } from "@/components/sidebar/nav-user";
import { Button } from "@/components/ui/button";
import { SidebarProvider } from "@/components/ui/sidebar";
import { NavigationProvider } from "@/contexts/navigation-context";
import { cn } from '@/lib/utils';
import { useLicense } from '@/hooks/useLicense';
import { Bell, MessageCircle, Settings2 } from "lucide-react";
import React, { useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

interface LayoutProps {
    children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
    const { projectId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { hasAIFeatures } = useLicense();
    const pathname = location.pathname;

    // --- Page Configuration Logic ---
    const pageConfig = useMemo(() => {
        // Regex helpers
        const isChapterEditorPage = /\/chapters\/./.test(pathname) && !pathname.includes('plan-scenes');
        const isTemplatesListPage = /\/templates\/?$/.test(pathname) || /\/form-builder\/?$/.test(pathname);

        // Feature detection
        const isAIPage = pathname.includes('/ai-chat');
        const isMindMapPage = pathname.includes('mind-maps');
        const isTimelinePage = pathname.includes('/timeline');
        const isProgressTrackerPage = pathname.includes('/progress');
        const isOverviewPage = pathname.endsWith('/overview') || (projectId && pathname.endsWith(projectId));

        // Group 1: Pages that should NOT have the standard internal scroll container (they handle their own scrolling)
        // This effectively sets "overflow-hidden" on the main container
        const disableLayoutScroll =
            isAIPage ||
            pathname.includes('/ai-prompts') ||
            isChapterEditorPage ||
            isMindMapPage ||
            isTimelinePage ||
            pathname.includes('/thoughts') ||
            pathname.includes('/notes') ||
            isTemplatesListPage ||
            pathname.includes('/settings') ||
            pathname.includes('/elements') ||
            pathname.includes('/tags');

        // Exception: Explicitly allow scroll for these
        const hasInternalScroll = disableLayoutScroll && !isProgressTrackerPage && !isOverviewPage;

        // Group 2: Pages that hide the top header (Editor/Canvas modes)
        const hideHeader = isChapterEditorPage || isTimelinePage || isMindMapPage;

        // Group 3: Pages that have a specific Right Sidebar
        const hasRightSidebar = isChapterEditorPage;

        return {
            disableLayoutScroll: hasInternalScroll,
            hideHeader,
            hasRightSidebar,
            isMindMapPage,
            isTimelinePage,
            isChapterEditorPage
        };
    }, [pathname, projectId]);

    const { hideHeader, hasRightSidebar } = pageConfig;

    return (
        <NavigationProvider projectId={projectId}>
            {/* Outer SidebarProvider for the MAIN Left Sidebar (AppSidebar) */}
            <SidebarProvider>
                <div className="flex bg-sidebar h-screen w-full overflow-hidden">
                    <AppSidebar />

                    {/* Main Application Area */}
                    <main className="flex-1 flex flex-col h-screen overflow-hidden relative">
                        {/* Top Header - Conditionally rendered */}
                        {!hideHeader && (
                            <div className="h-16 flex items-center justify-between px-6 flex-shrink-0 z-20">
                                <div className="flex items-center">
                                    <PageTitle />
                                </div>

                                <div className="flex items-center gap-3">
                                    {hasAIFeatures && (
                                        <ChatSheet trigger={
                                            <Button variant="ghost" size="icon" className="h-9 w-9 rounded-full bg-background border shadow-sm text-muted-foreground hover:text-foreground hover:bg-muted/50">
                                                <MessageCircle className="h-4 w-4" />
                                            </Button>
                                        } />
                                    )}
                                    <Button variant="ghost" size="icon" className="h-9 w-9 rounded-full bg-background border shadow-sm text-muted-foreground hover:text-foreground hover:bg-muted/50">
                                        <Bell className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-9 w-9 rounded-full bg-background border shadow-sm text-muted-foreground hover:text-foreground hover:bg-muted/50"
                                        onClick={() => navigate(projectId ? `/projects/${projectId}/settings` : '/account')}
                                        title="Settings"
                                    >
                                        <Settings2 className="h-4 w-4" />
                                    </Button>
                                    <NavUser />
                                </div>
                            </div>
                        )}

                        {/* Content Wrapper */}
                        <div className="flex-1 flex overflow-hidden relative">
                            {/* Inner Content Structure */}
                            {hasRightSidebar ? (
                                /* RightSidebarSwitcher manages its own open/close state */
                                <RightSidebarSwitcher>
                                    <MainContentWrapper pageConfig={pageConfig}>
                                        {children}
                                    </MainContentWrapper>
                                </RightSidebarSwitcher>
                            ) : (
                                /* Standard Pages - No extra provider */
                                <MainContentWrapper pageConfig={pageConfig}>
                                    {children}
                                </MainContentWrapper>
                            )}
                        </div>
                    </main>
                </div>
            </SidebarProvider>
        </NavigationProvider>
    );
}

interface PageConfig {
    disableLayoutScroll: boolean;
    isChapterEditorPage: boolean;
    isMindMapPage: boolean;
    isTimelinePage: boolean;
}

// We have replaced RightSidebarLayout entirely with RightSidebarSwitcher which is imported at the top

/**
 * Reusable wrapper for the main content area to avoid duplication between conditional branches
 */
function MainContentWrapper({ children, pageConfig }: { children: React.ReactNode, pageConfig: PageConfig }) {
    const { disableLayoutScroll, isChapterEditorPage, isMindMapPage, isTimelinePage } = pageConfig;

    return (
        <div className={cn(
            "flex-1 overflow-hidden flex flex-col relative pl-2 pb-2",
            (isMindMapPage || isTimelinePage || isChapterEditorPage) ? "mt-4" : ""
        )}>
            <div className="flex-1 w-full overflow-hidden flex flex-col relative bg-background rounded-[24px] shadow-sm border min-h-0">
                <div
                    className={cn(
                        "flex-1 w-full relative flex flex-col min-h-0",
                        disableLayoutScroll ? "overflow-hidden" : "overflow-y-scroll scrollbar-hide"
                    )}
                    style={{ WebkitOverflowScrolling: 'touch' }}
                >
                    <div className={cn(
                        "w-full mx-auto flex-1 flex flex-col min-h-0",
                        (!isChapterEditorPage && !isTimelinePage && !isMindMapPage) ? "max-w-[1920px]" : ""
                    )}>
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
}
